<template>
  <div class="relative" ref="menuRoot" data-account-user-menu>
    <!-- Trigger: circular initial badge + device name + invite label on desktop -->
    <button
      type="button"
      class="flex items-center space-x-2 p-1 rounded-lg dark:hover:bg-gray-700 hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
      :class="{ 'bg-blue-50 dark:bg-gray-700': isOpen && !uiStore.tvMode }"
      @click="isOpen = !isOpen"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      :aria-label="ariaLabel"
      :title="uiStore.tvMode ? '' : titleText"
    >
      <span
        class="inline-flex items-center justify-center w-8 h-8 rounded-full bg-blue-600 text-white text-sm font-medium select-none"
      >
        {{ initial }}
      </span>
      <span class="hidden md:flex flex-col items-start leading-tight pr-1">
        <span class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ displayName }}</span>
        <span v-if="showSecondaryLabel" class="text-xs text-gray-500 dark:text-gray-400">{{ secondaryLabel }}</span>
      </span>
    </button>

    <!-- Pair-remote modal — rendered inside AccountMenu so the trigger is
         co-located with the rest of the device-management UI. -->
    <RemotePairModal v-if="showPairModal" @close="showPairModal = false" />

    <!-- Dropdown menu.
         data-tv-row makes spatial nav treat the dropdown items as a
         vertical "row" so arrow keys move within the menu, and
         data-modal-open scopes spatial nav inside the dropdown in TV
         mode (without it the next/prev candidates include the muted
         buttons in the page behind the menu, so D-pad would silently
         escape the menu before the user could pick "Manage invites"). -->
    <div
      v-if="isOpen"
      ref="dropdownRef"
      role="menu"
      data-tv-row="account-menu"
      :data-modal-open="remoteNavActive ? '' : null"
      class="account-menu__dropdown absolute right-0 top-full mt-2 w-72 max-w-[calc(100vw-1rem)] max-h-[calc(100dvh-4.5rem)] overflow-y-auto overscroll-contain bg-white dark:bg-gray-900 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-800 z-50"
    >
      <!-- Header summary: avatar + name + subtitle -->
      <div class="account-menu__header px-4 pt-4 pb-3 border-b border-gray-200 dark:border-gray-800">
        <div class="flex items-center gap-3">
          <span
            class="account-menu__header-avatar inline-flex items-center justify-center w-10 h-10 rounded-full bg-blue-600 text-white text-sm font-semibold select-none flex-shrink-0"
          >
            {{ initial }}
          </span>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">{{ displayName }}</div>
            <div v-if="showSecondaryLabel" class="text-xs text-gray-500 dark:text-gray-400 truncate">{{ summaryLabel }}</div>
          </div>
        </div>

        <!-- Account stats — Put.io username, storage, plan, parallel
             downloads, monthly bandwidth. Discreet small-text under
             the admin/session line. Was the standalone header
             dropdown before; merged so the avatar menu is the single
             chrome entry point. Only shown when the putio API
             responded with disk info. -->
        <div
          v-if="canViewAccountStats && putioInfo?.disk"
          class="mt-3 space-y-1.5 text-[11px] text-gray-500 dark:text-gray-400"
        >
          <div v-if="putioInfo.username" class="flex items-center justify-between gap-3">
            <span class="truncate">Put.io account</span>
            <span class="text-gray-700 dark:text-gray-300 truncate ml-2">{{ putioInfo.username }}</span>
          </div>
          <div class="flex items-center justify-between gap-3">
            <span class="truncate">Storage</span>
            <span class="text-gray-700 dark:text-gray-300 tabular-nums">
              {{ formatSize(putioInfo.disk.used) }} / {{ formatSize(putioInfo.disk.size) }}
            </span>
          </div>
          <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 overflow-hidden">
            <div
              class="bg-blue-500 h-full"
              :style="{ width: storagePercent + '%' }"
            ></div>
          </div>
          <div v-if="monthlyBandwidthText" class="flex items-center justify-between gap-3 pt-1">
            <span>Bandwidth this month</span>
            <span class="text-gray-700 dark:text-gray-300 tabular-nums">{{ monthlyBandwidthText }}</span>
          </div>
          <div v-if="planExpiresText" class="flex items-center justify-between gap-3">
            <span>Plan</span>
            <span class="text-gray-700 dark:text-gray-300">expires {{ planExpiresText }}</span>
          </div>
          <div v-if="parallelDownloads != null" class="flex items-center justify-between gap-3">
            <span>Parallel downloads</span>
            <span class="text-gray-700 dark:text-gray-300 tabular-nums">{{ parallelDownloads }}</span>
          </div>

          <!-- Empty trash sits at the bottom of the stats panel so the
               freeable space is one tap away from the storage bar that
               motivates it. Compact text-xs row styling matches the
               other stats lines visually (no chunky action-button
               padding) while still reading as interactive. -->
          <button
            v-if="putioInfo?.trash_size > 0"
            type="button"
            role="menuitem"
            class="account-menu__item mt-1.5 w-full flex items-center justify-between gap-3 rounded-md px-2 py-1.5 text-xs text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
            @click="onEmptyTrash"
            :disabled="emptyingTrash"
          >
            <span class="flex items-center gap-2">
              <svg class="account-menu__icon w-3.5 h-3.5 text-red-400 dark:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              <span>{{ emptyingTrash ? 'Emptying…' : 'Empty trash' }}</span>
            </span>
            <span class="tabular-nums opacity-75">{{ formatSize(putioInfo.trash_size) }}</span>
          </button>
        </div>
      </div>

      <div class="py-1">
        <!-- "Set display name" button + inline edit form previously
             lived here; moved to Settings > Account so the device
             rename UI sits next to the other account-level pickers
             (theme, region/language). -->

        <!-- Order (per user): Manage invites · Downloads · Monitoring ·
             Link TV · Switch to TV mode · (Pair phone) · Theme · Settings ·
             Log out. Link TV + Pair phone are conditional and slotted next
             to the TV-mode toggle they relate to. -->

        <!-- Admin-only: Manage invites -->
        <RouterLink
          v-if="authStore.isAdmin"
          role="menuitem"
          to="/admin/tokens"
          class="account-menu__item flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isOpen = false"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207"/>
          </svg>
          Manage invites
        </RouterLink>

        <!-- Downloads — quick access to the transfer list. Only shown when the
             invite can add transfers (the same permission that gates the
             Downloads page itself), so library-only users don't see a dead link. -->
        <RouterLink
          v-if="authStore.canAccessDownloads"
          role="menuitem"
          to="/downloads"
          class="account-menu__item flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isOpen = false"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 3v12m0 0l-4-4m4 4l4-4M4 17v2a2 2 0 002 2h12a2 2 0 002-2v-2"/>
          </svg>
          Downloads
        </RouterLink>

        <!-- Monitoring — torrent/library availability watch list. Moved here
             from the Discover tab strip; gated on the same `discover`
             permission as its route so library-only invites don't see a
             dead link. -->
        <RouterLink
          v-if="authStore.hasPermission?.('discover')"
          role="menuitem"
          to="/discover/monitoring"
          class="account-menu__item flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isOpen = false"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
          </svg>
          Monitoring
        </RouterLink>

        <!-- Link TV — moved here from the main nav. Only shown when the
             current account has the capability (admin or full tier). -->
        <RouterLink
          v-if="authStore.canLinkDevice"
          role="menuitem"
          to="/link-device"
          class="account-menu__item flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isOpen = false"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" stroke-width="1.8" viewBox="0 0 24 24">
            <!-- Cast / broadcast-to-TV: a screen with wifi waves in the corner
                 — clearer "send this to my TV" affordance than the previous
                 monitor-with-stand glyph (which read as "open on desktop"). -->
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 6.75A2.25 2.25 0 0 1 6 4.5h12a2.25 2.25 0 0 1 2.25 2.25v9A2.25 2.25 0 0 1 18 18h-3.75"/>
            <path stroke-linecap="round" stroke-linejoin="round" d="M3.75 13.5a4.5 4.5 0 0 1 4.5 4.5M3.75 17.25a.75.75 0 1 0 0 1.5.75.75 0 0 0 0-1.5ZM3.75 9.75a8.25 8.25 0 0 1 8.25 8.25"/>
          </svg>
          Link TV
        </RouterLink>

        <!-- TV / Desktop mode toggle -->
        <button
          type="button"
          role="menuitem"
          class="account-menu__item w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="toggleTvMode"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25m18 0A2.25 2.25 0 0018.75 3H5.25A2.25 2.25 0 003 5.25m18 0V12a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 12V5.25"/>
          </svg>
          {{ uiStore.tvMode ? 'Switch to desktop mode' : 'Switch to TV mode' }}
        </button>

        <!-- Pair phone as remote (TV mode only). Switches to "End
             remote session" once a phone is connected, so the user
             always has a single entry point for pairing-related state. -->
        <button
          v-if="uiStore.tvMode && !remote.phoneConnected.value && authStore.canPairRemote"
          type="button"
          role="menuitem"
          class="account-menu__item w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="openPairRemote"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.5 1.5H8.25A2.25 2.25 0 006 3.75v16.5a2.25 2.25 0 002.25 2.25h7.5A2.25 2.25 0 0018 20.25V3.75a2.25 2.25 0 00-2.25-2.25H13.5m-3 0V3h3V1.5m-3 0h3m-3 18.75h3"/>
          </svg>
          Pair phone as remote…
        </button>
        <button
          v-else-if="uiStore.tvMode && remote.phoneConnected.value"
          type="button"
          role="menuitem"
          class="account-menu__item w-full flex items-center gap-3 px-4 py-2.5 text-sm text-emerald-600 dark:text-emerald-400 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition-colors"
          @click="endRemoteSession"
        >
          <svg class="account-menu__icon w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <circle cx="12" cy="12" r="9" fill="currentColor" opacity="0.2" />
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4"/>
          </svg>
          <span class="flex-1 text-left">Phone connected</span>
          <span class="text-xs text-gray-500 dark:text-gray-400">End</span>
        </button>

        <!-- Theme switcher — segmented Auto/Light/Dark.
             Lives in the dropdown for quick access; the same control is
             also surfaced on /settings, this is the shortcut. Hidden in
             TV mode because the cinematic surface force-applies dark
             theme (uiStore.applyTvModeClass adds `.dark` and the
             stylesheet sets `color-scheme: dark`), so the switcher
             would be a no-op control. -->
        <div
          v-if="!uiStore.tvMode"
          role="group"
          aria-label="Theme"
          class="account-menu__item flex items-center justify-between gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200"
        >
          <span class="flex items-center gap-3">
            <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
            </svg>
            Theme
          </span>
          <div class="account-menu__theme-seg inline-flex items-center rounded-full bg-gray-100 dark:bg-gray-800 p-0.5 ring-1 ring-gray-200 dark:ring-gray-700">
            <button
              v-for="opt in THEME_OPTIONS"
              :key="opt.value"
              type="button"
              :aria-label="opt.label"
              :title="opt.label"
              :aria-pressed="settingsStore.theme === opt.value"
              @click="setTheme(opt.value)"
              class="account-menu__theme-btn inline-flex items-center justify-center w-7 h-7 rounded-full transition-colors"
              :class="settingsStore.theme === opt.value
                ? 'bg-white dark:bg-gray-900 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'"
            >
              <!-- Auto icon: circle split light/dark -->
              <svg v-if="opt.value === 'auto'" class="w-3.5 h-3.5" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
                <path d="M8 1a7 7 0 100 14V1z"/>
                <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" stroke-width="1.5"/>
              </svg>
              <!-- Sun -->
              <svg v-else-if="opt.value === 'light'" class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
                <circle cx="12" cy="12" r="4"/>
                <path stroke-linecap="round" d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41"/>
              </svg>
              <!-- Moon -->
              <svg v-else class="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M21 12.79A9 9 0 1111.21 3 7 7 0 0021 12.79z"/>
              </svg>
            </button>
          </div>
        </div>

        <!-- Settings link — moved here from the header gear icon so
             everything chrome-related lives behind the avatar. -->
        <RouterLink
          role="menuitem"
          to="/settings"
          class="account-menu__item flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          @click="isOpen = false"
        >
          <svg class="account-menu__icon w-5 h-5 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
          Settings
        </RouterLink>

        <div class="my-1 border-t border-gray-200 dark:border-gray-800"></div>

        <!-- Log out -->
        <button
          type="button"
          role="menuitem"
          class="account-menu__item account-menu__item--danger w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition-colors disabled:opacity-50"
          @click="doLogout"
          :disabled="loggingOut"
        >
          <svg class="account-menu__icon w-5 h-5 text-red-400 dark:text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15.75 9V5.25A2.25 2.25 0 0013.5 3h-6a2.25 2.25 0 00-2.25 2.25v13.5A2.25 2.25 0 007.5 21h6a2.25 2.25 0 002.25-2.25V15M12 9l-3 3m0 0l3 3m-3-3h12.75"/>
          </svg>
          {{ loggingOut ? 'Logging out…' : 'Log out' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, defineAsyncComponent, onMounted, onBeforeUnmount, nextTick, watch } from 'vue'
import { RouterLink } from 'vue-router'
import { isRemoteNavPreferred } from '@/helpers/remote-nav'
import { useAuthStore } from '@/stores/auth'
import { useUiStore } from '@/stores/ui'
import { usePutioStore } from '@/stores/putio'
import { useSettingsStore } from '@/stores/settings'
import formatSize from '@/helpers/format-size'
// The phone-pairing modal (v-if="showPairModal") is only shown on an explicit
// "pair a remote" action, so load it on demand instead of in the always-mounted
// header. AccountMenu was its only eager importer.
const RemotePairModal = defineAsyncComponent(() => import('@/components/RemotePairModal.vue'))
import { useRemoteControl } from '@/composables/useRemoteControl'

const authStore = useAuthStore()
const uiStore = useUiStore()
const remoteNavActive = computed(() => isRemoteNavPreferred(uiStore))
const putioStore = usePutioStore()
const settingsStore = useSettingsStore()
// Singleton — same instance the modal sees, so `phoneConnected` flips
// here automatically when a phone joins the session.
const remote = useRemoteControl()

const isOpen = ref(false)
const editingName = ref(false)
const nameInput = ref('')
const nameInputEl = ref(null)
const nameError = ref('')
const savingName = ref(false)
const loggingOut = ref(false)
const showPairModal = ref(false)
const menuRoot = ref(null)
const dropdownRef = ref(null)
const emptyingTrash = ref(false)

// === Account stats (storage / plan) — pulled from the Pinia putio store.
// Was its own header dropdown; now lives discreetly under the account
// label inside the avatar menu.

const canViewAccountStats = computed(() => authStore.canViewAccountStats)
const putioInfo = computed(() => putioStore.info)

const storagePercent = computed(() => {
  const used = putioInfo.value?.disk?.used
  const total = putioInfo.value?.disk?.size
  if (!Number.isFinite(used) || !Number.isFinite(total) || total <= 0) return 0
  return Math.max(0, Math.min(100, Math.round((used / total) * 100)))
})

const planExpiresText = computed(() => {
  const ts = putioInfo.value?.plan_expiration_date || putioInfo.value?.account_active_until
  if (!ts) return null
  // Both fields can be either a unix timestamp (seconds) or an ISO string.
  let date = null
  if (typeof ts === 'number') date = new Date(ts * 1000)
  else if (typeof ts === 'string') date = new Date(ts)
  if (!date || Number.isNaN(date.getTime())) return null
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
})

const parallelDownloads = computed(() => {
  const n = putioInfo.value?.simultaneous_download_limit
  return Number.isFinite(n) ? n : null
})

// Monthly bandwidth display — Put.io reports usage as bytes-used and
// optional bytes-allowed. Format as a "X / Y" string when there's a
// quota, else just the used number.
const monthlyBandwidthText = computed(() => {
  const used = putioInfo.value?.monthly_bandwidth_usage
  const total = putioInfo.value?.monthly_bandwidth_quota
  if (!Number.isFinite(used) || used <= 0) return null
  if (Number.isFinite(total) && total > 0) {
    return `${formatSize(used)} / ${formatSize(total)}`
  }
  return formatSize(used)
})

// Lazy-load the putio info the first time the menu opens so the dropdown
// shows numbers; closed-menu cost is zero.
watch(isOpen, async (opened) => {
  if (!opened) return
  if (!canViewAccountStats.value) return
  if (putioInfo.value?.disk) return // already have it
  try {
    await putioStore.getInfo()
  } catch { /* non-fatal — the section just stays hidden */ }
})

// Mirror open state into the ui store so other chrome (TvSlimHeader)
// can keep itself visible while this dropdown is open. In TV mode also
// auto-focus the first menu item once the dropdown renders so D-pad
// users land on something inside the menu rather than the trigger.
watch(isOpen, async (opened) => {
  uiStore.setAccountMenuOpen(opened)
  if (!opened) {
    if (!remoteNavActive.value) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const trigger = menuRoot.value?.querySelector('button')
        if (!trigger || !document.contains(trigger)) return
        try { trigger.focus({ focusVisible: true }) }
        catch { trigger.focus() }
      })
    })
    return
  }
  if (!remoteNavActive.value) return
  await nextTick()
  const root = dropdownRef.value
  if (!root) return
  const first = root.querySelector('a, button:not([disabled]), [role="menuitem"]')
  if (!first) return
  try { first.focus({ focusVisible: true }) }
  catch { first.focus() }
})

// Close the avatar dropdown whenever the mobile hamburger opens.
// Without this, both menus could be open simultaneously on small
// viewports and visually stack on top of each other.
watch(
  () => uiStore.mobileMenuOpen,
  (open) => {
    if (open && isOpen.value) {
      isOpen.value = false
      editingName.value = false
      nameError.value = ''
    }
  },
)

onBeforeUnmount(() => {
  // If the menu was open when the component unmounts (e.g. logout/route
  // change), make sure the global flag clears so other chrome doesn't
  // stay pinned forever.
  if (uiStore.accountMenuOpen) uiStore.setAccountMenuOpen(false)
})

async function onEmptyTrash() {
  if (emptyingTrash.value) return
  emptyingTrash.value = true
  try {
    await putioStore.emptyTrash?.()
    await putioStore.getInfo()
  } catch (err) {
    console.warn('AccountMenu: empty trash failed', err)
  } finally {
    emptyingTrash.value = false
  }
}

function openPairRemote() {
  showPairModal.value = true
  isOpen.value = false
}

function endRemoteSession() {
  remote.stop()
  isOpen.value = false
}

// Admin sessions have no invite label and usually no deviceName — fall back
// to sensible strings so the menu never shows empty/"null" text.
const displayName = computed(() => {
  if (authStore.deviceName) return authStore.deviceName
  return authStore.isAdmin ? 'Admin' : 'Unnamed device'
})

// The second line is suppressed when the device name *is* the invite label
// (the default after redeem) so we don't show the same string twice. It only
// appears when the user has renamed their device, or for admin sessions where
// "Admin session" is meaningful context.
const secondaryLabel = computed(() => {
  if (authStore.label) return 'Invite: ' + authStore.label
  return authStore.isAdmin ? 'Admin session' : ''
})

const summaryLabel = secondaryLabel

const showSecondaryLabel = computed(() => {
  if (authStore.isAdmin && !authStore.label) return true
  if (!authStore.label) return false
  return authStore.deviceName && authStore.deviceName !== authStore.label
})

// Initial comes from the displayed name (deviceName or the fallback label)
// rather than the invite label — keeps it in sync with what's shown on screen.
const initial = computed(() => {
  const src = displayName.value || '?'
  const ch = String(src).trim().charAt(0)
  return ch ? ch.toUpperCase() : '?'
})

const ariaLabel = computed(() => `Account menu for ${displayName.value}`)
const titleText = computed(() => {
  return secondaryLabel.value
    ? `${displayName.value} · ${secondaryLabel.value}`
    : displayName.value
})

const canSetDeviceName = computed(() => authStore.status === 'unlocked')

function startEditName() {
  nameInput.value = authStore.deviceName || ''
  nameError.value = ''
  editingName.value = true
  nextTick(() => {
    try {
      nameInputEl.value?.focus()
      nameInputEl.value?.select?.()
    } catch { /* noop */ }
  })
}

function cancelEdit() {
  editingName.value = false
  nameError.value = ''
  savingName.value = false
}

async function saveName() {
  const trimmed = nameInput.value.trim()
  if (trimmed.length < 1) {
    nameError.value = 'Display name cannot be empty'
    return
  }
  if (trimmed.length > 64) {
    nameError.value = 'Display name must be 64 characters or fewer'
    return
  }
  nameError.value = ''
  savingName.value = true
  try {
    await authStore.setDeviceName(trimmed)
    editingName.value = false
    isOpen.value = false
  } catch (e) {
    nameError.value = e?.body?.error || e?.message || 'Failed to save'
  } finally {
    savingName.value = false
  }
}

function toggleTvMode() {
  uiStore.setTvMode(!uiStore.tvMode)
  isOpen.value = false
}

// Theme switcher options. Order matters -- the segmented control renders
// them left-to-right in this order, matching the icon vocabulary in the
// template (auto = split-circle, light = sun, dark = moon).
const THEME_OPTIONS = [
  { value: 'auto', label: 'Auto (system)' },
  { value: 'light', label: 'Light theme' },
  { value: 'dark', label: 'Dark theme' },
]

function setTheme(value) {
  settingsStore.setTheme(value)
  // Don't close the menu -- the user might want to compare themes
  // without reopening the dropdown.
}

async function doLogout() {
  if (loggingOut.value) return
  loggingOut.value = true
  // Close the menu before navigation so there's no flash of stale chrome.
  isOpen.value = false
  try {
    await authStore.logout()
  } catch {
    // authStore.logout() handles redirect and state cleanup even on error.
  } finally {
    loggingOut.value = false
  }
}

function onClickOutside(event) {
  if (!isOpen.value) return
  const root = menuRoot.value
  if (!root) return
  // Use composedPath() so we catch the click even when Vue has already
  // swapped the target node out of the DOM (e.g. "Set display name" replaces
  // itself with an inline form). Falling back to contains() for older browsers.
  const path = typeof event.composedPath === 'function' ? event.composedPath() : null
  const inside = path ? path.includes(root) : root.contains(event.target)
  if (!inside) {
    isOpen.value = false
    editingName.value = false
    nameError.value = ''
  }
}

function onKeydown(event) {
  if (event.key !== 'Escape') return
  if (editingName.value) {
    cancelEdit()
    return
  }
  if (isOpen.value) {
    isOpen.value = false
  }
}

onMounted(() => {
  document.addEventListener('click', onClickOutside)
  document.addEventListener('keydown', onKeydown)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', onClickOutside)
  document.removeEventListener('keydown', onKeydown)
})
</script>

<!-- TV-mode overrides for the inline theme picker. The global
     `.tv-mode button { min-height: 64px; padding: 0.75rem 1rem }` rule
     was stretching the 28x28 segmented-control buttons into huge
     oblongs and crushing the icons. These are inline picker controls,
     NOT D-pad navigation primaries, so they opt out of the 64px floor.
     Bump the SVG icons to ~22-26px so they read clearly at 10ft. -->
<style>
/* Phones: the dropdown is `absolute right-0`, i.e. its right edge is
   pinned to the avatar TRIGGER — which sits well left of the viewport
   edge in a narrow header. The 18rem panel then extends LEFT and runs
   off x=0 (a `max-w` cap only shrinks width, it can't move the anchor).
   The header is `position: sticky; top: 0`, so a viewport-anchored
   `fixed` panel tracks the trigger's row exactly while guaranteeing the
   panel never leaves the screen. Width still collapses via the
   `max-w-[calc(100vw-1rem)]` utility on very narrow screens. */
@media (max-width: 639px) {
  .account-menu__dropdown {
    position: fixed !important;
    top: 4rem !important;
    right: 0.5rem !important;
    left: auto !important;
    margin-top: 0 !important;
  }
}
.tv-mode .account-menu__theme-btn {
  min-height: 0 !important;
  min-width: 0 !important;
  width: 2.5rem !important;
  height: 2.5rem !important;
  padding: 0 !important;
}
.tv-mode .account-menu__theme-btn svg {
  width: 1.25rem !important;
  height: 1.25rem !important;
  min-width: 1.25rem !important;
  min-height: 1.25rem !important;
}
.tv-mode .account-menu__theme-seg {
  padding: 0.25rem !important;
}
</style>
